import { Application } from '@/types/application';
import { ArityScope } from '@/types/arity';
import { Detail } from '@/types/detail';
import { ConfigurationType, ListParameter, Metadata } from '@/types/metadata/metadata';
import { Subroutine } from '@/types/subroutine';
import { findEntrySubroutine } from './blocks';
import { isNumber } from './typeGuards';

const ARITY_BOUND: ConfigurationType[] = ['arguments', 'returnvariables', 'returnvalues', 'appreturnvalues'];

export const isArityBound = (parameter: ListParameter): boolean => ARITY_BOUND.includes(parameter.type);

export const arityScope = (application: Application, metadata: Metadata): ArityScope => ({
  subroutines: application.subroutines,
  nodes: application.blocks.nodes,
  metadata
});

export const boundReference = (
  parameter: ListParameter,
  values: Record<string, unknown> | undefined
): number | undefined => {
  const reference = parameter.subtype ? values?.[parameter.subtype] : undefined;
  return isNumber(reference) ? reference : undefined;
};

export const boundSubroutine = (
  parameter: ListParameter,
  values: Record<string, unknown> | undefined,
  nodeId: number,
  scope: ArityScope
): Subroutine | undefined => {
  switch (parameter.type) {
    case 'appreturnvalues':
      return findEntrySubroutine(scope.subroutines, scope.nodes, scope.metadata);
    case 'returnvalues':
      return scope.subroutines.find((subroutine) => subroutine.nodes.includes(nodeId));
    default: {
      const reference = boundReference(parameter, values);
      return scope.subroutines.find((subroutine) => subroutine.id === reference);
    }
  }
};

export const listArity = (
  parameter: ListParameter,
  values: Record<string, unknown> | undefined,
  nodeId: number,
  scope: ArityScope
): number | undefined => {
  if (!isArityBound(parameter)) return undefined;

  const bound = boundSubroutine(parameter, values, nodeId, scope);
  return parameter.type === 'arguments' ? bound?.args.length : bound?.return_types.length;
};

// Fills the slots the interface declares but the list does not carry. Rows beyond it are kept to be removed by hand.
export const padToArity = (values: Detail[], arity: number): unknown[] =>
  Array.from({ length: Math.max(arity, values.length) }, (_, index) => values[index] ?? '');
